import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'modal',
    'iframe',
    'title',
    'imageViewer',
    'downloadBtn',
    'sidePanel',
    'messagesSection',
    'messagesContent',
    'fileList'
  ]

  static values = {
    autoOpenData: Object
  }

  static PREVIEWABLE_EXTENSIONS = new Set([
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'gif'
  ])

  fileExtension(filename) {
    return (filename || '').split('.').pop().toLowerCase()
  }

  static IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif'])

  isPreviewable(filename) {
    return this.constructor.PREVIEWABLE_EXTENSIONS.has(this.fileExtension(filename))
  }

  isImage(filename) {
    return this.constructor.IMAGE_EXTENSIONS.has(this.fileExtension(filename))
  }

  fileIconClass(filename) {
    const ext = this.fileExtension(filename)
    const icons = {
      pdf: 'fa-file-pdf text-danger',
      doc: 'fa-file-word text-primary',
      docx: 'fa-file-word text-primary',
      xls: 'fa-file-excel text-success',
      xlsx: 'fa-file-excel text-success',
      csv: 'fa-file-csv text-success',
      png: 'fa-file-image text-info',
      jpg: 'fa-file-image text-info',
      jpeg: 'fa-file-image text-info',
      gif: 'fa-file-image text-info',
      zip: 'fa-file-zipper text-warning',
      txt: 'fa-file-lines text-secondary'
    }
    return icons[ext] || 'fa-file text-secondary'
  }

  // Sort attachments: previewable files (PDF/images) first, then most recently updated
  sortAttachments(attachments) {
    const sorted = [...attachments].sort((a, b) => {
      const aPreviewable = this.isPreviewable(a.name) ? 0 : 1
      const bPreviewable = this.isPreviewable(b.name) ? 0 : 1
      if (aPreviewable !== bPreviewable) return aPreviewable - bPreviewable

      // Within the same group, sort by most recently updated first
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
      return bTime - aTime
    })

    return this.markRecentlyUpdated(sorted)
  }

  // Mark the most recently updated file as 'updated' if it was updated
  // more than 5 minutes after the next most recently updated file
  markRecentlyUpdated(attachments) {
    if (attachments.length < 2) return attachments

    const withTimes = attachments
      .map((att, index) => ({ index, time: att.updatedAt ? new Date(att.updatedAt).getTime() : 0 }))
      .filter((item) => item.time > 0)
      .sort((a, b) => b.time - a.time)

    if (withTimes.length < 2) return attachments

    const newest = withTimes[0]
    const secondNewest = withTimes[1]
    const fiveMinutes = 5 * 60 * 1000

    if (newest.time - secondNewest.time > fiveMinutes) {
      attachments[newest.index] = { ...attachments[newest.index], recentlyUpdated: true }
    }

    return attachments
  }

  connect() {
    this.currentAttachments = []
    this.currentIndex = 0
    this.pendingAttachment = null

    if (this.hasModalTarget) {
      // Load the PDF after the modal is fully visible so the viewer
      // can calculate zoom from the actual container dimensions.
      this.modalTarget.addEventListener('shown.bs.modal', () => {
        if (this.pendingAttachment) {
          this.loadFile(this.pendingAttachment)
          this.pendingAttachment = null
        }
      })

      // Clear iframe when modal is hidden
      this.modalTarget.addEventListener('hidden.bs.modal', () => {
        this.clearIframe()
      })
    }

    // Auto-open a package using server-provided data (no DOM dependency).
    this.autoOpenFromData()
  }

  autoOpenFromData() {
    const data = this.autoOpenDataValue
    if (!data || !data.attachments || data.attachments.length === 0) return

    // Clear to prevent re-triggering
    this.autoOpenDataValue = {}
    history.replaceState(null, '', window.location.pathname)

    const sorted = this.sortAttachments(data.attachments)
    this.currentAttachments = sorted
    this.currentIndex = 0

    this.updateTitle(data.title || 'Financial Package')

    this.pendingAttachment = sorted[0]
    this.updateDownloadBtn(sorted[0].downloadUrl)
    this.updateSidePanel(data.sentEmails || [], data.periodEnding || '', sorted)
    this.showModal()
  }

  // Open with a single PDF (legacy support)
  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = event.params.url
    const title = event.params.title || 'PDF Preview'
    const downloadUrl = event.params.downloadUrl || url

    if (!url) {
      console.warn('No PDF URL provided')
      return
    }

    const attachment = { name: title, url: url, downloadUrl: downloadUrl }
    this.currentAttachments = [attachment]
    this.currentIndex = 0

    this.updateTitle(title)
    this.hideSidePanel()
    this.pendingAttachment = attachment
    this.updateDownloadBtn(downloadUrl)
    this.showModal()
  }

  // Open with multiple attachments (package card)
  openPackage(event) {
    // Don't trigger if clicking on a button or link inside the card
    if (event.target.closest('a, button')) {
      return
    }

    event.preventDefault()

    const title = event.params.title || 'Financial Package'
    const attachments = event.params.attachments
    const sentEmails = event.params.sentEmails || []
    const periodEnding = event.params.periodEnding || ''

    if (!attachments || attachments.length === 0) {
      console.warn('No attachments provided')
      return
    }

    const sorted = this.sortAttachments(attachments)
    this.currentAttachments = sorted
    this.currentIndex = 0

    this.updateTitle(title)

    this.pendingAttachment = sorted[0]
    this.updateDownloadBtn(sorted[0].downloadUrl)
    this.updateSidePanel(sentEmails, periodEnding, sorted)
    this.showModal()
  }

  switchFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)

    if (index === this.currentIndex) return

    this.currentIndex = index
    const attachment = this.currentAttachments[index]

    this.loadFile(attachment)
    this.updateDownloadBtn(attachment.downloadUrl)
    this.updateActiveFile(index)
  }

  updateTitle(title) {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = title
    }
  }

  loadFile(attachment) {
    if (!this.hasIframeTarget) return

    if (this.isImage(attachment.name) && this.hasImageViewerTarget) {
      this.iframeTarget.classList.add('d-none')
      this.iframeTarget.src = 'about:blank'
      this.imageViewerTarget.src = attachment.url
      this.imageViewerTarget.alt = attachment.name
      this.imageViewerTarget.classList.remove('d-none')
      this.hideDownloadPlaceholder()
    } else if (this.isPreviewable(attachment.name)) {
      this.hideImageViewer()
      this.iframeTarget.classList.remove('d-none')
      this.iframeTarget.src = attachment.url
      this.hideDownloadPlaceholder()
    } else {
      this.hideImageViewer()
      this.iframeTarget.classList.add('d-none')
      this.iframeTarget.src = 'about:blank'
      this.showDownloadPlaceholder(attachment)
    }
  }

  hideImageViewer() {
    if (this.hasImageViewerTarget) {
      this.imageViewerTarget.classList.add('d-none')
      this.imageViewerTarget.src = ''
    }
  }

  showDownloadPlaceholder(attachment) {
    let placeholder = this.element.querySelector('.financial-packages-download-placeholder')
    if (!placeholder) {
      placeholder = document.createElement('div')
      placeholder.className = 'financial-packages-download-placeholder'
      this.iframeTarget.parentNode.appendChild(placeholder)
    }
    placeholder.innerHTML = `
      <div class="text-center text-muted py-5">
        <i class="fas ${this.fileIconClass(attachment.name)} mb-3" style="font-size: 3rem;"></i>
        <p class="mb-3">${attachment.name}</p>
        <p class="small mb-3">This file type cannot be previewed in the browser.</p>
        <a href="${attachment.downloadUrl}" target="_blank" class="btn btn-primary btn-sm">
          <i class="fas fa-download me-1"></i>Download File
        </a>
      </div>
    `
    placeholder.classList.remove('d-none')
  }

  hideDownloadPlaceholder() {
    const placeholder = this.element.querySelector('.financial-packages-download-placeholder')
    if (placeholder) {
      placeholder.classList.add('d-none')
    }
  }

  updateDownloadBtn(url) {
    if (this.hasDownloadBtnTarget) {
      this.downloadBtnTarget.href = url
    }
  }

  updateSidePanel(sentEmails, periodEnding, attachments) {
    if (!this.hasSidePanelTarget) return

    // Always show the side panel (it has files at minimum)
    this.sidePanelTarget.classList.remove('d-none')

    // Sent emails
    if (this.hasMessagesSectionTarget && this.hasMessagesContentTarget) {
      if (sentEmails && sentEmails.length > 0) {
        this.messagesSectionTarget.classList.remove('d-none')
        this.messagesContentTarget.innerHTML = sentEmails
          .map((email) => {
            const date = new Date(email.sentAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
            return `
              <div class="mb-2 border rounded p-2">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="fw-semibold">${email.sentBy || 'Bookkeeper360'}</small>
                  <small class="text-muted">${date}</small>
                </div>
                <div class="small financial-packages-rich-notes">
                  ${email.bodyHtml}
                </div>
              </div>
            `
          })
          .join('')
      } else {
        this.messagesSectionTarget.classList.add('d-none')
      }
    }

    // File list
    if (this.hasFileListTarget) {
      const filesHtml = attachments
        .map(
          (att, index) => `
        <a class="financial-packages-side-panel-file ${index === 0 ? 'active' : ''}"
           href="#"
           data-action="click->pdf-preview#switchFile"
           data-index="${index}">
          <i class="fas ${this.fileIconClass(att.name)}" style="flex-shrink: 0;"></i>
          <span class="financial-packages-side-panel-file-name">${att.name}</span>
          ${att.recentlyUpdated ? '<span class="financial-packages-updated-badge">Updated</span>' : ''}
        </a>
      `
        )
        .join('')
      this.fileListTarget.innerHTML = filesHtml
    }
  }

  hideSidePanel() {
    if (this.hasSidePanelTarget) {
      this.sidePanelTarget.classList.add('d-none')
    }
  }

  updateActiveFile(activeIndex) {
    if (!this.hasFileListTarget) return

    const files = this.fileListTarget.querySelectorAll('.financial-packages-side-panel-file')
    files.forEach((file, index) => {
      if (index === activeIndex) {
        file.classList.add('active')
      } else {
        file.classList.remove('active')
      }
    })
  }

  showModal() {
    if (this.hasModalTarget) {
      const modal = new bootstrap.Modal(this.modalTarget)
      modal.show()
    }
  }

  clearIframe() {
    if (this.hasIframeTarget) {
      this.iframeTarget.src = 'about:blank'
      this.iframeTarget.classList.remove('d-none')
    }
    this.hideImageViewer()
    this.hideDownloadPlaceholder()
    this.currentAttachments = []
    this.currentIndex = 0
  }

  close() {
    if (this.hasModalTarget) {
      const modal = bootstrap.Modal.getInstance(this.modalTarget)
      if (modal) {
        modal.hide()
      }
    }
  }
}
